/** 承認キュー画面の判定ロジック（純粋関数。画面から切り離してテストできるようにする） */

import type { PostChannelRow, PostRow, QueueItem } from './postTypes'

/** キューを画面のセクションごとに振り分ける */
export function splitQueue(items: QueueItem[]) {
  const by = (...st: string[]) => items.filter(i => st.includes(i.post.status))
  const scheduled = by('scheduled').sort((a, b) =>
    (a.post.scheduledAt ?? '').localeCompare(b.post.scheduledAt ?? ''))
  const stock = by('approved').sort((a, b) => b.post.createdAt.localeCompare(a.post.createdAt))
  return {
    working: by('generating'),
    /** 失敗＋一部だけ投稿済み（残りの媒体の承認・結果不明の解消・再投稿をここで行う — Codex 実装レビュー #8） */
    failed: by('failed', 'partially_published'),
    approval: by('pending_approval', 'draft'),
    /** 予約済み（日時の早い順）→ ストック（新しい順） */
    stock: [...scheduled, ...stock],
    publishing: by('publishing'),
    /** 投稿済みだが、確かめてほしいことが残っている投稿（サーバーは注意のある投稿済みだけを返す） */
    noted: by('published'),
  }
}

/** Guard がまだ現在の版に対して終わっていない媒体 */
export function isGuardPending(ch: PostChannelRow): boolean {
  return ch.guardCurrent === false || ch.status === 'pending'
}

/** GATED_OUT のうち、まだ人が個別承認していない媒体 */
export function needsGatedApproval(ch: PostChannelRow): boolean {
  return ch.status === 'gated_out' && !ch.humanApprovedBy
}

/**
 * 承認系ボタン（ストックへ・予約・今すぐ投稿）を押せない理由。押せるなら null。
 * サーバーも同じ条件で 400 を返すが、押す前に理由を見せて「押しても無反応」を避ける（audit C11）
 */
export function approveBlockReason(post: Pick<PostRow, 'status' | 'errorMessage'>, channels: PostChannelRow[]): string | null {
  // 画像の不備は、本文を直しても解消しない。直し方（作り直す）をそのまま見せる
  if (needsRecreate(post)) return post.errorMessage ?? null
  if (post.status === 'draft') return '要修正: 本文を編集してください'
  if (channels.some(isGuardPending)) return '安全チェック中です（チェックが終わると承認できます）'
  if (channels.some(c => c.status === 'blocked')) return '投稿不可の媒体があります。本文を編集して再チェックしてください'
  if (channels.some(needsGatedApproval)) return '要確認の媒体があります。理由を確認して個別に承認してください'
  return null
}

/**
 * 本文の編集では直せず、作り直すしかない要修正か（Instagram に出す画像が無い・縦横比が合わない）。
 * サーバーの instagramImageProblem が付ける文言の先頭で見分ける
 */
export function needsRecreate(post: Pick<PostRow, 'status' | 'errorMessage'>): boolean {
  return post.status === 'draft' && (post.errorMessage ?? '').startsWith('Instagram に出せません')
}

/** 失敗した投稿を「再投稿」できるか（本文の版が無い＝生成失敗は再投稿できない — audit C3） */
export function canRetryPublish(item: QueueItem): boolean {
  return item.activeVersion != null
}

/** 生成失敗の「作り直す」リンク（同じお題・種別・ペルソナで投稿作成画面を開く） */
export function composeRetryHref(post: Pick<PostRow, 'title' | 'kind' | 'personaId'>): string {
  const params = new URLSearchParams()
  if (post.title) params.set('topic', post.title)
  if (post.kind === 'tips' || post.kind === 'product') params.set('kind', post.kind)
  if (post.personaId) params.set('personaId', post.personaId)
  const qs = params.toString()
  return qs ? `/compose?${qs}` : '/compose'
}

export function kindLabel(kind: string): string {
  return kind === 'tips' ? 'お役立ち' : kind === 'product' ? '商品紹介' : kind
}
